if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[data-country-search-results]'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', () => {
            this.hidePanel();
            this.elements.button.focus();
          });
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      connectedCallback() {
        // Editor section replacement can change the header instance.
        this.header = document.querySelector('.header-wrapper');
        this.drawer = this.closest('.menu-drawer');
        this.onDocumentClick = this.closeSelector.bind(this);
        this.onViewportChange = () => {
          const returnFocus = this.elements.panel.contains(document.activeElement);
          this.hidePanel();
          if (returnFocus) this.elements.button.focus();
        };
        document.addEventListener('click', this.onDocumentClick);
        this.mql.addEventListener('change', this.onViewportChange);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        this.mql.removeEventListener('change', this.onViewportChange);
        this.hidePanel();
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', '');
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
        }
        if (this.ownsScrollLock) document.body.classList.remove('overflow-hidden-mobile');
        this.ownsScrollLock = false;
        this.drawer?.classList.remove('country-selector-open');
        if (this.header) {
          this.header.preventHide = !!this.header.querySelector(
            'localization-form button[aria-expanded="true"], .yw-menu-toggle[aria-expanded="true"], header-menu details[open]'
          );
        }
      }

      onContainerKeyDown(event) {
        const key = event.code.toUpperCase();
        if (!['ARROWUP', 'ARROWDOWN'].includes(key) || this.elements.button.getAttribute('aria-expanded') !== 'true') return;
        event.preventDefault();
        const items = Array.from(this.querySelectorAll('a')).filter((item) => !item.closest('[hidden], .hidden'));
        if (!items.length) return;
        const index = items.indexOf(document.activeElement);
        const next = key === 'ARROWUP' ? (index > 0 ? index - 1 : items.length - 1) : (index + 1) % items.length;
        items[next].focus();
      }

      onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE' || this.elements.button.getAttribute('aria-expanded') !== 'true') return;
        event.preventDefault();
        this.hidePanel();
        event.stopPropagation();
        this.elements.button.focus();
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        if (this.elements.button.getAttribute('aria-expanded') === 'true') {
          this.hidePanel();
          return;
        }
        document.querySelectorAll('localization-form').forEach((form) => {
          if (form !== this && form.elements?.button.getAttribute('aria-expanded') === 'true') form.hidePanel();
        });
        this.elements.button.focus();
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');
        // Inline mobile navigation scrolls within the menu; only the legacy country overlay locks the page.
        if (this.elements.panel.classList.contains('country-selector') && !this.mql.matches &&
            !this.hasAttribute('data-inline-localization') &&
            !document.body.classList.contains('overflow-hidden-tablet') &&
            !document.body.classList.contains('overflow-hidden-mobile')) {
          document.body.classList.add('overflow-hidden-mobile');
          this.ownsScrollLock = true;
        }
        if (this.elements.search && this.mql.matches) this.elements.search.focus();
        if (this.hasAttribute('data-prevent-hide') && this.header) this.header.preventHide = true;
        this.drawer?.classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (this.elements.button.getAttribute('aria-expanded') !== 'true') return;
        const outside = event.type === 'focusout' ? !this.contains(event.relatedTarget) : !this.contains(event.target);
        if (outside || event.target.classList.contains('country-selector__overlay')) {
          const returnFocus = event.type === 'click' && this.elements.panel.contains(document.activeElement);
          this.hidePanel();
          if (returnFocus) this.elements.button.focus();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('.countries a');
        let visibleCountries = 0;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.textContent = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
